import { toApiError } from './api-error.ts'
import { api } from './api.ts'

export interface ViewerAccess {
  displayAccount: boolean
  pageIds: string[]
  /** Pages a TV may show. An older answer omits this, and then it is empty. */
  displayablePageIds: string[]
  unavailablePageIds: string[]
  reason?: string
}

/**
 * Read one answer from `GET /viewer-access`.
 *
 * A malformed answer cannot grant access or render a page.
 *
 * @param answer - The parsed JSON body.
 * @returns The viewer's access.
 */
export function parseViewerAccess(answer: unknown): ViewerAccess {
  if (
    typeof answer !== 'object' ||
    answer === null ||
    !('pageIds' in answer) ||
    !('unavailablePageIds' in answer) ||
    !('displayAccount' in answer)
  ) {
    throw new Error('Invalid viewer access response')
  }
  const { pageIds, unavailablePageIds, displayAccount } = answer
  const displayablePageIds = readStringList(
    'displayablePageIds' in answer ? answer.displayablePageIds : []
  )
  if (
    typeof displayAccount !== 'boolean' ||
    !Array.isArray(pageIds) ||
    !pageIds.every(id => typeof id === 'string') ||
    !Array.isArray(unavailablePageIds) ||
    !unavailablePageIds.every(id => typeof id === 'string') ||
    displayablePageIds === null
  ) {
    throw new Error('Invalid viewer access response')
  }
  const reason = 'reason' in answer ? answer.reason : undefined
  return typeof reason === 'string'
    ? {
        displayAccount,
        pageIds,
        displayablePageIds,
        unavailablePageIds,
        reason
      }
    : { displayAccount, pageIds, displayablePageIds, unavailablePageIds }
}

/**
 * @param value - A candidate list of page ids.
 * @returns The list, or null when it is not one.
 */
function readStringList(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every(id => typeof id === 'string')) {
    return null
  }
  return value
}

/**
 * Ask the backend which pages the signed-in employee may open.
 *
 * @returns The viewer's access.
 * @throws {ApiRequestError} when the backend refuses or does not answer.
 * @throws {Error} when the body arrived and the parser cannot read it.
 */
export async function fetchViewerAccess(): Promise<ViewerAccess> {
  let body: unknown
  try {
    body = await api.get('viewer-access').json()
  } catch (error) {
    throw await toApiError(error)
  }
  return parseViewerAccess(body)
}
